import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import { useSchoolDays } from '../../hooks/useSchoolDays';

export default function TeacherTimetable() {
  // School days hook — determines which columns to show
  const { dayNames: activeDayNames } = useSchoolDays();

  const [myClass, setMyClass] = useState(null);
  const [entries, setEntries] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editCell, setEditCell] = useState(null);
  const [form, setForm] = useState({ subject: '', start_time: '', end_time: '' });

  const [configOpen, setConfigOpen] = useState(false);
  const [configPeriods, setConfigPeriods] = useState([]);
  const [configSubjects, setConfigSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const c = await api.get('/teacher/my-class');
      setMyClass(c.data);

      if (c.data?.class_id) {
        const [t, cfg] = await Promise.all([
          api.get('/timetable/'),
          api.get(`/timetable/config/${c.data.class_id}`),
        ]);
        setEntries(t.data || []);
        setPeriods(cfg.data.periods || []);
        setSubjects(cfg.data.subjects || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEntry = (day, period) =>
    entries.find(e => e.day === day && e.period === period);

  // ===== CELL EDIT =====
  const handleCellClick = (day, period) => {
    const existing = getEntry(day, period);
    const periodInfo = periods.find(p => p.n === period);
    setEditCell({ day, period });
    setForm({
      subject: existing?.subject || (subjects[0] || 'Mathematics'),
      start_time: existing?.start_time || periodInfo?.start || '',
      end_time: existing?.end_time || periodInfo?.end || '',
    });
  };

  const handleSaveCell = async () => {
    const existing = getEntry(editCell.day, editCell.period);

    // "Break" removes the entry
    if (form.subject === 'Break') {
      if (existing) await api.delete(`/timetable/${existing.id}`);
      setEditCell(null);
      fetchData();
      return;
    }

    try {
      if (existing) {
        await api.put(`/timetable/${existing.id}`, {
          subject: form.subject,
          start_time: form.start_time,
          end_time: form.end_time,
        });
      } else {
        await api.post('/timetable/', {
          class_id: myClass.class_id,
          day: editCell.day,
          period: editCell.period,
          subject: form.subject,
          start_time: form.start_time,
          end_time: form.end_time,
        });
      }
      setEditCell(null);
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear all timetable entries for your class?')) return;
    for (const e of entries) {
      await api.delete(`/timetable/${e.id}`);
    }
    fetchData();
  };

  // ===== CONFIG (Periods & Subjects) =====
  const openConfig = () => {
    setConfigPeriods(periods.map(p => ({ ...p })));
    setConfigSubjects([...subjects]);
    setConfigOpen(true);
  };

  const addPeriod = () => {
    const last = configPeriods[configPeriods.length - 1];
    const n = last ? last.n + 1 : 1;
    setConfigPeriods([
      ...configPeriods,
      { n, start: last?.end || '08:00', end: '' }
    ]);
  };

  const removePeriod = (n) => {
    if (configPeriods.length <= 1) {
      alert('At least one period is required');
      return;
    }
    if (!window.confirm(`Remove Period ${n}? Any existing entries in this period will be deleted.`)) return;
    setConfigPeriods(configPeriods.filter(p => p.n !== n));
  };

  const updatePeriod = (idx, field, value) => {
    const next = [...configPeriods];
    next[idx][field] = value;
    setConfigPeriods(next);
  };

  const reorderPeriods = () => {
    const renumbered = configPeriods.map((p, i) => ({ ...p, n: i + 1 }));
    setConfigPeriods(renumbered);
  };

  const addSubject = () => {
    const s = newSubject.trim();
    if (!s) return;
    if (configSubjects.includes(s)) {
      alert('This subject already exists');
      return;
    }
    setConfigSubjects([...configSubjects, s]);
    setNewSubject('');
  };

  const removeSubject = (s) => {
    if (s === 'Break') {
      alert('"Break" is reserved and cannot be removed');
      return;
    }
    if (configSubjects.length <= 1) {
      alert('At least one subject is required');
      return;
    }
    if (!window.confirm(`Remove subject "${s}"? Any existing entries using it will remain but show as "${s}".`)) return;
    setConfigSubjects(configSubjects.filter(x => x !== s));
  };

  const moveSubject = (idx, dir) => {
    const next = [...configSubjects];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setConfigSubjects(next);
  };

  const saveConfig = async () => {
    for (const p of configPeriods) {
      if (!p.start || !p.end) {
        alert(`Period ${p.n} is missing start or end time`);
        return;
      }
    }

    setSavingConfig(true);
    try {
      const renumbered = configPeriods.map((p, i) => ({ ...p, n: i + 1 }));
      await api.post(`/timetable/config/${myClass.class_id}`, {
        periods: renumbered,
        subjects: configSubjects,
      });
      setConfigOpen(false);
      fetchData();
      alert('✅ Configuration saved');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset periods and subjects to defaults? This will overwrite your custom setup.')) return;
    api.post(`/timetable/config/${myClass.class_id}`, {
      periods: [
        { n: 1, start: '08:00', end: '08:45' },
        { n: 2, start: '08:45', end: '09:30' },
        { n: 3, start: '09:30', end: '10:15' },
        { n: 4, start: '10:30', end: '11:15' },
        { n: 5, start: '11:15', end: '12:00' },
        { n: 6, start: '12:00', end: '12:45' },
        { n: 7, start: '13:30', end: '14:15' },
        { n: 8, start: '14:15', end: '15:00' },
      ],
      subjects: [
        'Mathematics', 'English', 'Urdu', 'Science', 'Social Studies',
        'Islamiat', 'Computer', 'Physical Education', 'Art', 'Library', 'Break'
      ],
    }).then(() => {
      setConfigOpen(false);
      fetchData();
    });
  };

  const getSubjectColor = (subj) => {
    const map = {
      'Mathematics': 'bg-blue-100 text-blue-800',
      'English': 'bg-green-100 text-green-800',
      'Urdu': 'bg-yellow-100 text-yellow-800',
      'Science': 'bg-purple-100 text-purple-800',
      'Social Studies': 'bg-orange-100 text-orange-800',
      'Islamiat': 'bg-emerald-100 text-emerald-800',
      'Computer': 'bg-indigo-100 text-indigo-800',
      'Physical Education': 'bg-red-100 text-red-800',
      'Art': 'bg-pink-100 text-pink-800',
      'Library': 'bg-teal-100 text-teal-800',
    };
    return map[subj] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="bg-white p-6 rounded shadow text-center">Loading...</div>;
  if (!myClass?.class_name) return <div className="bg-white p-6 rounded shadow text-center">No class assigned.</div>;

  // Build grid columns from school days only
  const DAYS = activeDayNames.length > 0
    ? activeDayNames
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  return (
    <div>
      {/* HEADER */}
      <div className="bg-white p-5 rounded shadow mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            📅 Class Timetable: {myClass.class_name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Click any cell to add/edit · Use the ⚙️ button to manage periods &amp; subjects
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Showing {DAYS.length} school day{DAYS.length !== 1 ? 's' : ''}: {DAYS.join(', ')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openConfig}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            <i className="fas fa-cog mr-1"></i> Manage Periods &amp; Subjects
          </button>
          <button
            onClick={fetchData}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            <i className="fas fa-sync-alt mr-1"></i> Refresh
          </button>
          {entries.length > 0 && (
            <button
              onClick={handleClearAll}
              className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              <i className="fas fa-trash mr-1"></i> Clear All
            </button>
          )}
        </div>
      </div>

      {/* GRID — columns are school days only */}
      <div className="bg-white p-4 rounded shadow overflow-x-auto">
        <table className="w-full min-w-[800px] border-collapse">
          <thead>
            <tr>
              <th className="p-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 w-28 border">
                Period
              </th>
              {DAYS.map(d => (
                <th key={d} className="p-2 bg-gray-100 text-center text-xs font-semibold text-gray-600 border">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map(p => (
              <tr key={p.n}>
                <td className="p-2 bg-gray-50 border">
                  <div className="text-xs font-semibold text-gray-700">Period {p.n}</div>
                  <div className="text-xs text-gray-400">{p.start}–{p.end}</div>
                </td>
                {DAYS.map(d => {
                  const entry = getEntry(d, p.n);
                  return (
                    <td
                      key={d}
                      onClick={() => handleCellClick(d, p.n)}
                      className="p-1 border cursor-pointer hover:bg-gray-50"
                    >
                      {entry ? (
                        <div className={`p-2 rounded text-center text-xs font-medium ${getSubjectColor(entry.subject)}`}>
                          {entry.subject}
                        </div>
                      ) : (
                        <div className="p-2 text-center text-xs text-gray-300">+ Add</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CELL EDIT MODAL */}
      {editCell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editCell.day} · Period {editCell.period}
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Subject</label>
                <select
                  className="w-full p-2 border rounded"
                  value={form.subject}
                  onChange={e => setForm({ ...form, subject: e.target.value })}
                >
                  {subjects.map(s => <option key={s}>{s}</option>)}
                </select>
                {form.subject === 'Break' && (
                  <p className="text-xs text-red-500 mt-1">"Break" removes this period entry</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Start</label>
                  <input
                    type="time"
                    className="w-full p-2 border rounded"
                    value={form.start_time}
                    onChange={e => setForm({ ...form, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End</label>
                  <input
                    type="time"
                    className="w-full p-2 border rounded"
                    value={form.end_time}
                    onChange={e => setForm({ ...form, end_time: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setEditCell(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={handleSaveCell}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIG MODAL */}
      {configOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">⚙️ Manage Periods &amp; Subjects</h2>
              <button onClick={() => setConfigOpen(false)} className="text-2xl text-gray-400 hover:text-gray-700">×</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* PERIODS */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800">
                    🕐 Periods <span className="text-xs text-gray-500">({configPeriods.length})</span>
                  </h3>
                  <button
                    onClick={addPeriod}
                    className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    <i className="fas fa-plus mr-1"></i> Add Period
                  </button>
                </div>

                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {configPeriods.map((p, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 border rounded bg-gray-50">
                      <span className="text-xs font-bold text-gray-600 w-6">#{idx + 1}</span>
                      <input
                        type="time"
                        className="flex-1 p-1 border rounded text-xs"
                        value={p.start}
                        onChange={e => updatePeriod(idx, 'start', e.target.value)}
                      />
                      <span className="text-xs text-gray-400">→</span>
                      <input
                        type="time"
                        className="flex-1 p-1 border rounded text-xs"
                        value={p.end}
                        onChange={e => updatePeriod(idx, 'end', e.target.value)}
                      />
                      <button
                        onClick={() => removePeriod(p.n)}
                        className="text-red-500 hover:text-red-700 px-1"
                        title="Remove"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* SUBJECTS */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">
                  📚 Subjects <span className="text-xs text-gray-500">({configSubjects.length})</span>
                </h3>

                <div className="flex gap-2 mb-3">
                  <input
                    className="flex-1 p-2 border rounded text-sm"
                    placeholder="New subject name..."
                    value={newSubject}
                    onChange={e => setNewSubject(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addSubject(); }}
                  />
                  <button
                    onClick={addSubject}
                    className="px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>

                <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                  {configSubjects.map((s, idx) => (
                    <div key={s} className="flex items-center gap-2 p-2 border rounded bg-white">
                      <span className={`w-3 h-3 rounded flex-shrink-0 ${getSubjectColor(s).split(' ')[0]}`}></span>
                      <span className="flex-1 text-sm text-gray-800">{s}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveSubject(idx, -1)}
                          disabled={idx === 0}
                          className="text-gray-400 hover:text-gray-700 px-1 disabled:opacity-30"
                        >
                          <i className="fas fa-arrow-up text-xs"></i>
                        </button>
                        <button
                          onClick={() => moveSubject(idx, 1)}
                          disabled={idx === configSubjects.length - 1}
                          className="text-gray-400 hover:text-gray-700 px-1 disabled:opacity-30"
                        >
                          <i className="fas fa-arrow-down text-xs"></i>
                        </button>
                        <button
                          onClick={() => removeSubject(s)}
                          className="text-red-500 hover:text-red-700 px-1"
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t">
              <button
                onClick={resetToDefaults}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <i className="fas fa-undo mr-1"></i> Reset to Defaults
              </button>
              <div className="flex gap-2">
                <button
                  className="px-4 py-2 bg-gray-300 rounded"
                  onClick={() => setConfigOpen(false)}
                  disabled={savingConfig}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  onClick={saveConfig}
                  disabled={savingConfig}
                >
                  {savingConfig ? 'Saving...' : <><i className="fas fa-save mr-1"></i> Save Configuration</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}